import type { AccountIdParsed, ApiNetwork } from '../api/types';
import type { Account, AccountType } from '../global/types';

import { APP_NAME } from '../config';
import { escapeStringRegexp } from './regex';
import { shortenAddress } from './shortenAddress';

export function parseAccountId(accountId: string): AccountIdParsed {
  const parts = accountId.split('-');
  const network: ApiNetwork = parts.includes('testnet') ? 'testnet' : 'mainnet';

  const primaryId = Number(parts[0]);
  if (Number.isFinite(primaryId)) {
    return { id: primaryId, network };
  }

  const legacyIdPart = [...parts].reverse().find((part) => /^\d+$/.test(part));
  const legacyId = legacyIdPart ? Number(legacyIdPart) : NaN;

  return {
    id: Number.isFinite(legacyId) ? legacyId : 0,
    network,
  };
}

export function buildAccountId(account: AccountIdParsed) {
  const { id, network } = account;
  return `${id}-${network}`;
}

export function getMainAccountAddress(byChain: Account['byChain']) {
  return (byChain.ton ?? Object.values(byChain).find(Boolean))?.address;
}

export function getAccountTitle(account: Account) {
  return account.title || shortenAddress(getMainAccountAddress(account.byChain) ?? '');
}

export function generateAccountTitle(params: {
  accounts: Record<string, Account>;
  accountType: AccountType;
  network: ApiNetwork;
  titlePostfix?: string;
}) {
  const { accounts, accountType, network, titlePostfix } = params;
  const accountAmount = Object.keys(accounts).length;
  const isMainnet = network === 'mainnet';

  // Handle first account special case
  if (accountAmount === 0) {
    return isMainnet ? APP_NAME : `Testnet ${APP_NAME}`;
  }

  // Count wallets by type
  const walletCounts = Object.values(accounts).reduce(
    (acc, wallet) => {
      if (wallet.type === 'view') acc.view++;
      if (wallet.type === 'hardware') acc.hardware++;
      if (wallet.type === 'mnemonic') acc.mnemonic++;
      return acc;
    },
    { view: 0, hardware: 0, mnemonic: 0 },
  );

  const walletTypeConfig: Record<AccountType, { prefix: string; count: string | number }> = {
    view: { prefix: 'Wallet', count: walletCounts.view + 1 },
    hardware: { prefix: 'Ledger', count: `#${walletCounts.hardware + 1}` },
    // Other brands fall back to the plain noun, the way Air names wallets outside My Wallet
    mnemonic: { prefix: 'TWallet', count: walletCounts.mnemonic + 1 },
  };

  const config = walletTypeConfig[accountType];
  const networkPrefix = isMainnet ? '' : 'Testnet ';
  const postfix = titlePostfix ? ` ${titlePostfix}` : '';

  return `${networkPrefix}${config.prefix} ${config.count}${postfix}`;
}

export function generateNextSubwalletTitle(baseTitle: string, accounts: Record<string, Account>) {
  // Ensure the base ends with a number so the result reads "Wallet 1.1" instead of "Wallet.1"
  const normalizedBase = /\d$/.test(baseTitle) ? baseTitle : `${baseTitle} 1`;

  // Match existing "{normalizedBase}.{number}" accounts to find the next available number
  const pattern = new RegExp(`^${escapeStringRegexp(normalizedBase)}\\.(\\d+)$`);

  const existingNums = Object.values(accounts)
    .map((acc) => acc.title?.match(pattern))
    .filter(Boolean)
    .map((m) => Number(m[1]));

  const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : 1;

  return `${normalizedBase}.${nextNum}`;
}
